import { useState, useRef, useEffect, useCallback } from "react";
import { useToast } from "@/components/base/Toast";
import { transcriptData, aiSummaryData, callNotesData, highlightedKeywords } from "@/mocks/callsData";
import type { CallRecord, TranscriptLine, AISummary, CallNote } from "@/mocks/callsData";

import { fetchApi } from "@/services/apiService";

interface Props {
  call: any;
  onClose: () => void;
  onReload?: () => void;
}

const agents: Record<string, string> = {
  "Rajesh Kumar": "RK",
  "Priya Sharma": "PS",
  "Amit Patel": "AP",
  "Sneha Rao": "SR",
};

const statusBadge: Record<string, string> = {
  Connected: "bg-green-50 text-green-700",
  Completed: "bg-green-50 text-green-700",
  Missed: "bg-red-50 text-red-600",
  Busy: "bg-amber-50 text-amber-700",
  "No Answer": "bg-red-50 text-red-600",
};

function formatTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function highlightKeywords(text: string): React.ReactNode {
  let result: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    let earliestIdx = -1;
    let earliestWord = "";

    for (const kw of highlightedKeywords) {
      const idx = remaining.toLowerCase().indexOf(kw.toLowerCase());
      if (idx !== -1 && (earliestIdx === -1 || idx < earliestIdx)) {
        earliestIdx = idx;
        earliestWord = kw;
      }
    }

    if (earliestIdx === -1) {
      result.push(<span key={key++}>{remaining}</span>);
      break;
    }

    if (earliestIdx > 0) {
      result.push(<span key={key++}>{remaining.slice(0, earliestIdx)}</span>);
    }

    const actualWord = remaining.slice(earliestIdx, earliestIdx + earliestWord.length);
    result.push(
      <span key={key++} className="bg-yellow-100 rounded px-0.5">{actualWord}</span>
    );
    remaining = remaining.slice(earliestIdx + earliestWord.length);
  }

  return <>{result}</>;
}

export default function CallDetailDrawer({ call, onClose, onReload }: Props) {
  const { showToast } = useToast();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [transcriptSearch, setTranscriptSearch] = useState("");
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState<CallNote[]>(() => {
    const list: CallNote[] = [];
    if (call.notes) {
      list.push({
        text: call.notes,
        author: call.agent || "Agent",
        time: call.date || "Recent",
      });
    }
    if (callNotesData[Number(call.id)]) {
      list.push(...callNotesData[Number(call.id)]);
    }
    return list;
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const transcript: TranscriptLine[] = transcriptData[Number(call.id)] || [];
  const summary: AISummary | undefined = aiSummaryData[Number(call.id)];
  const duration = call.durationSeconds || 60;

  const filteredTranscript = transcriptSearch.trim()
    ? transcript.filter(
        (l) =>
          l.text.toLowerCase().includes(transcriptSearch.toLowerCase()) ||
          l.speakerName.toLowerCase().includes(transcriptSearch.toLowerCase())
      )
    : transcript;

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev + 1;
          if (next >= duration) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setPlaying(false);
            return duration;
          }
          return next;
        });
      }, 100);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing, duration]);

  const togglePlay = useCallback(() => setPlaying((p) => !p), []);

  const handleSkip = useCallback(
    (dir: "back" | "forward") => {
      setProgress((prev) => {
        const next = dir === "back" ? prev - 10 : prev + 10;
        return Math.max(0, Math.min(duration, next));
      });
    },
    [duration]
  );

  const handleSaveNote = async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    const authorName = localStorage.getItem("user_name") || call.agent || "Agent";
    setNotes((prev) => [
      ...prev,
      { text: trimmed, author: authorName, time: "Just now" },
    ]);
    setNoteText("");

    try {
      await fetchApi(`/calls/${call.id}`, {
        method: "PUT",
        body: JSON.stringify({ notes: trimmed }),
      });
      showToast("Note saved successfully", "success");
      if (onReload) onReload();
    } catch {
      showToast("Note saved locally", "info");
    }
  };

  const waveformBars = Array.from({ length: 50 }, (_, i) => {
    const seed = Math.sin(i * 1.7) * 0.4 + Math.sin(i * 0.7) * 0.35 + 0.25;
    return Math.max(0.08, Math.min(1, seed));
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-[400px] bg-white shadow-xl flex flex-col animate-slide-in overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-[#111827]">{call.leadName}</h3>
            <p className="text-sm text-[#6b7280] mt-0.5">{call.phone}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Meta row */}
        <div className="flex items-center flex-wrap gap-3 px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-[10px] font-bold text-indigo-600">{agents[call.agent] || "?"}</span>
            </div>
            <span className="text-sm text-[#374151]">{call.agent}</span>
          </div>
          <span className="text-xs text-[#9ca3af]">{call.date}</span>
          <span className="text-xs text-[#6b7280] font-medium">{call.duration}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadge[call.status]}`}>
            {call.status}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Section 1: Recording Player */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
            <h4 className="text-sm font-semibold text-[#111827] mb-3">Call Recording</h4>

            {/* Waveform */}
            <div className="flex items-end gap-px h-16 w-full mb-4 px-1">
              {waveformBars.map((h, i) => {
                const barPct = (i / 49) * 100;
                const isPlayed = barPct <= (progress / duration) * 100;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-t-sm transition-colors duration-100 ${
                      isPlayed ? "bg-green-500" : "bg-gray-200"
                    }`}
                    style={{ height: `${h * 100}%` }}
                  />
                );
              })}
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSkip("back")}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                >
                  <i className="ri-skip-back-fill text-base" />
                </button>
                <button
                  onClick={togglePlay}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
                >
                  <i className={`text-lg ${playing ? "ri-pause-fill" : "ri-play-fill"}`} />
                </button>
                <button
                  onClick={() => handleSkip("forward")}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                >
                  <i className="ri-skip-forward-fill text-base" />
                </button>
              </div>

              <span className="text-sm text-[#6b7280] font-medium">
                {formatTime(progress)} / {formatTime(duration)}
              </span>
            </div>

            {/* Speed selector + Download */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-1">
                {[0.75, 1, 1.25, 1.5].map((s) => (
                  <button
                    key={s}
                    onClick={() => { setSpeed(s); showToast(`Speed set to ${s}x`, "info"); }}
                    className={`text-xs rounded px-2 py-0.5 transition-colors whitespace-nowrap ${
                      speed === s
                        ? "bg-indigo-600 text-white"
                        : "text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {s}x
                  </button>
                ))}
              </div>

              <button
                onClick={() => showToast("Recording download started", "success")}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 text-gray-600 text-xs rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-download-line text-xs" /> Download Recording
              </button>
            </div>
          </div>

          {/* Section 2: Transcript */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-sm font-semibold text-[#111827]">Transcript</h4>
              <span className="bg-purple-50 text-purple-700 text-xs rounded px-2 py-0.5 font-medium">AI Generated</span>
            </div>

            <input
              type="text"
              value={transcriptSearch}
              onChange={(e) => setTranscriptSearch(e.target.value)}
              placeholder="Search transcript..."
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-1 focus:ring-indigo-300 mb-3"
            />

            <div className="space-y-3">
              {filteredTranscript.length === 0 && (
                <p className="text-xs text-[#9ca3af] text-center py-4">No matching transcript lines</p>
              )}
              {filteredTranscript.map((line, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-xs text-[#9ca3af] flex-shrink-0 text-right w-8">{line.time}</span>
                  <div className="flex-1 min-w-0">
                    <span
                      className={`text-sm font-semibold ${
                        line.speaker === "agent" ? "text-indigo-700" : "text-[#374151]"
                      }`}
                    >
                      {line.speakerName}:
                    </span>{" "}
                    <span className="text-sm text-[#374151]">{highlightKeywords(line.text)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: AI Summary */}
          {summary && (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
              <div className="flex items-center gap-2 mb-3">
                <h4 className="text-sm font-semibold text-[#111827]">AI Summary</h4>
                <span className="bg-purple-50 text-purple-700 text-xs rounded px-2 py-0.5 font-medium">AI Generated</span>
              </div>

              {/* Key Points */}
              <div className="space-y-2 mb-4">
                {summary.keyPoints.map((pt, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600 text-sm mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#374151]">{pt}</span>
                  </div>
                ))}
              </div>

              {/* Sentiment */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs text-[#6b7280]">Customer Sentiment:</span>
                  <span className="bg-green-50 text-green-700 text-xs rounded px-2 py-0.5 font-semibold">{summary.sentimentLabel}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#9ca3af]">Negative</span>
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${summary.sentimentScore}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-[#9ca3af]">Positive</span>
                </div>
              </div>

              {/* Next Action */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="text-sm text-[#374151] mb-2">{summary.nextAction}</p>
                <button
                  onClick={() => showToast("Task created successfully", "success")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line" /> Create Task
                </button>
              </div>
            </div>
          )}

          {/* Section 4: Call Notes */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
            <h4 className="text-sm font-semibold text-[#111827] mb-3">Call Notes</h4>

            {notes.map((note, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="flex items-start justify-between mb-1">
                  <span className="text-xs font-semibold text-[#374151]">{note.author}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#9ca3af]">{note.time}</span>
                    <button
                      onClick={() => showToast("Edit note coming soon", "info")}
                      className="text-gray-400 hover:text-indigo-600 transition-colors"
                    >
                      <i className="ri-edit-line text-xs" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-[#6b7280]">{note.text}</p>
              </div>
            ))}

            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note about this call..."
              className="w-full border border-gray-200 rounded-lg p-3 text-sm text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-1 focus:ring-indigo-300 resize-none mb-2"
              rows={3}
            />
            <button
              onClick={handleSaveNote}
              disabled={!noteText.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-save-line" /> Save Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}